import { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import {
  useUpdateAgentApi,
  makeUpdateAgentRequest,
  agentResponseToAgent,
} from "@/hooks/api"
import { Agent, AgentType, Workspace } from "@/types"
import type { KnowledgeBaseFolder } from "@/hooks/api"
import FormFactory from "./FormFactory"
import { BasicFormType } from "./type"
import { VersionNoteDialog } from "./VersionNoteDialog"

export interface EditDrawerProps {
  agent: Agent
  workspace: Workspace
  knowledgeBaseMap: Map<string, KnowledgeBaseFolder>
  open: boolean
  onOpenChange?: (open: boolean) => void
  onUpdated?: (agent: Agent) => void
}

const EditDrawer = ({
  agent,
  workspace,
  knowledgeBaseMap,
  open,
  onOpenChange,
  onUpdated,
}: EditDrawerProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()

  const [disabledApply, setDisabledApply] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showVersionNoteDialog, setShowVersionNoteDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<BasicFormType | null>(
    null
  )

  // APIs
  const {
    error: updateBasicAgentError,
    data: updateBasicAgentRespData,
    fetchFn: updateBasicAgent,
  } = useUpdateAgentApi(agent?.id.toString() || "")

  useEffect(() => {
    if (!open) return
    setIsProcessing(false)
    setDisabledApply(true)
    setPendingFormData(null)
  }, [open])

  // basic agent error handling
  useEffect(() => {
    if (!updateBasicAgentError) return
    console.error("Failed to update agent", updateBasicAgentError)
    // Reset processing state on error
    // Keep pendingFormData so user can retry without re-entering version note
    setIsProcessing(false)
    alertToast({
      title: t("workspace.message.updateAgentFailed"),
      description: getUserFacingApiErrorMessage(
        updateBasicAgentError,
        t,
        updateBasicAgentRespData?.message ||
          t("workspace.message.updateAgentFailed")
      ),
    })
  }, [updateBasicAgentError, updateBasicAgentRespData, alertToast, t])

  const handleConfirmClick = (
    _agentType: AgentType,
    formData: BasicFormType
  ) => {
    // Store form data and show version note dialog
    setPendingFormData(formData)
    setShowVersionNoteDialog(true)
  }

  const handleVersionNoteConfirm = (versionNote: string) => {
    if (!pendingFormData) return
    setShowVersionNoteDialog(false)
    setIsProcessing(true)
    handleUpdateBasicAgent(pendingFormData, versionNote)
  }

  const handleVersionNoteCancel = () => {
    setShowVersionNoteDialog(false)
    setPendingFormData(null)
  }

  const handleUpdateBasicAgent = (
    formData: BasicFormType,
    versionNote: string = ""
  ) => {
    const req = makeUpdateAgentRequest(
      formData.name,
      formData.description,
      formData.systemPrompt,
      formData.unifiedTools,
      formData.attachments,
      formData.llmModel,
      formData.llmModelSettings,
      versionNote,
      formData.suggestedQuestions,
      formData.humanHandoff,
      formData.outputPresentationPolicy,
      formData.responseMode,
      formData.aiTakeoverGreeting
    )
    updateBasicAgent({
      jsonBody: req,
      callbackFn: (resp) => {
        if (!resp || !resp.data) {
          // Handle empty/malformed response - this is an unexpected state
          console.error("Update agent API returned empty or invalid response")
          setIsProcessing(false)
          alertToast({
            title: t("workspace.message.updateAgentFailed"),
            description: t(
              "workspace.message.unexpectedError",
              "An unexpected error occurred"
            ),
          })
          return
        }
        try {
          const updatedAgent = agentResponseToAgent(resp.data)
          setIsProcessing(false)
          setPendingFormData(null)
          successToast({
            title: t("workspace.agent.message.updateSuccess.title"),
            description: t(
              "workspace.agent.message.updateSuccess.description",
              {
                name: updatedAgent.name,
              }
            ),
          })
          onUpdated?.(updatedAgent)
        } catch (error) {
          console.error("Failed to parse update agent response", error)
          setIsProcessing(false)
          alertToast({
            title: t("workspace.message.updateAgentFailed"),
            description: t(
              "workspace.message.unexpectedError",
              "An unexpected error occurred"
            ),
          })
        }
      },
    })
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex flex-col overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="text-wrap break-all flex items-center">
              <span>Basic Agent</span>
            </SheetTitle>
            <SheetDescription>
              {t("workspace.sourceDetailDrawer.description")}
            </SheetDescription>
          </SheetHeader>

          <FormFactory
            agentType={agent.type}
            agent={agent}
            workspace={workspace}
            knowledgeBaseMap={knowledgeBaseMap}
            agents={[]}
            onDirtyChange={(dirty) => {
              setDisabledApply(!dirty)
            }}
            onConfirm={handleConfirmClick}
          >
            {/* Footer */}
            <SheetFooter className="gap-2">
              <SheetClose asChild>
                <Button variant="outline" disabled={isProcessing}>
                  {t("workspace.close")}
                </Button>
              </SheetClose>
              <Button type="submit" disabled={disabledApply || isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    {"Processing..."}
                  </>
                ) : (
                  t("workspace.save")
                )}
              </Button>
            </SheetFooter>
          </FormFactory>
        </SheetContent>
      </Sheet>

      <VersionNoteDialog
        open={showVersionNoteDialog}
        onOpenChange={setShowVersionNoteDialog}
        onConfirm={handleVersionNoteConfirm}
        onCancel={handleVersionNoteCancel}
        isLoading={isProcessing}
      />
    </>
  )
}

export { EditDrawer }
export default EditDrawer
